import React, { useCallback, useEffect, useRef, useState } from 'react';
import CrmDrawer from './CrmDrawer';
import CrmButton from './CrmButton';
import ConfirmDialog from './ConfirmDialog';
import './crm-ui.scss';

/** Shared add/edit shell for simple and medium domain forms. */
export default function CrmFormDrawer({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  mode = 'create',
  dirty = false,
  canSave = true,
  children,
  size = 'md',
  saveLabel,
}) {
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => { if (open) setServerError(''); }, [open]);

  const requestClose = useCallback(() => {
    if (saving) return;
    if (dirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose?.();
  }, [dirty, onClose, saving]);

  const discardAndClose = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose?.();
  }, [onClose]);

  const submit = async (event) => {
    event?.preventDefault?.();
    if (saving || !canSave) return;
    setSaving(true);
    setServerError('');
    try { await onSubmit?.(); }
    catch (error) {
      if (mounted.current) setServerError(error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Unable to save.');
    } finally { if (mounted.current) setSaving(false); }
  };

  return (
    <>
    <CrmDrawer
      open={open}
      onClose={requestClose}
      title={title}
      subtitle={subtitle}
      size={size}
      closeOnBackdrop={!saving}
      closeOnEscape={!saving}
      className="crm-form-drawer"
      footer={<CrmButton type="submit" form="crm-form-drawer-form" loading={saving} disabled={!canSave || saving}>{saveLabel || (mode === 'edit' ? 'Save changes' : 'Create')}</CrmButton>}
    >
      <form id="crm-form-drawer-form" className="crm-form-drawer__form" onSubmit={submit} noValidate>
        {serverError ? <div className="crm-form-drawer__error" role="alert">{serverError}</div> : null}
        {children}
      </form>
    </CrmDrawer>
    <ConfirmDialog
      open={discardConfirmOpen}
      title="Discard changes?"
      message="You have unsaved changes. Discard them and close this form?"
      confirmText="Discard changes"
      onConfirm={discardAndClose}
      onCancel={() => setDiscardConfirmOpen(false)}
    />
    </>
  );
}
